import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField, useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useRef, useState } from 'react';

const INPUT_DEBOUNCE_MS = 300;

interface Props {
  filterStr: string;
  onFilterStrChange: (value: string) => void;
}

const SearchInput: React.FC<Props> = ({ filterStr, onFilterStrChange }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(filterStr);
  const lastSearchValueRef = useRef<string | null>(null);

  // filterStr changing can mean two different things: the URL catching up to what we just typed
  // (ignore it, our local text is newer), or a real external change like Clear All / Back-Forward
  // (apply it). lastSearchValueRef holds the value we last sent, so we can tell them apart.
  useEffect(() => {
    if (filterStr === lastSearchValueRef.current) {
      lastSearchValueRef.current = null;
      return;
    }
    setInputValue(filterStr);
  }, [filterStr]);

  useEffect(() => {
    if (inputValue === filterStr) return;
    const timer = setTimeout(() => {
      lastSearchValueRef.current = inputValue;
      onFilterStrChange(inputValue);
    }, INPUT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, onFilterStrChange]);

  const handleClear = () => {
    setInputValue('');
    onFilterStrChange('');
  };

  return (
    <TextField
      id="search-input"
      variant="outlined"
      size="small"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  fill: inputValue !== '' ? theme.palette.text.primary : theme.palette.text.secondary
                }}
              />
            </InputAdornment>
          ),
          endAdornment:
            inputValue !== '' ? (
              <InputAdornment position="end">
                <IconButton id="clear-search-button" onClick={handleClear} aria-label={translation.clear}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined
        }
      }}
      placeholder={translation.search}
      onChange={(event) => setInputValue(event.target.value)}
      value={inputValue}
      sx={{
        width: { sm: '300px' },
        flexGrow: { xs: '1', sm: 'initial' },
        height: '40px',
        '& .MuiOutlinedInput-root': { backgroundColor: theme.custom.background.level3 }
      }}
    />
  );
};

export default SearchInput;
